import { ChangeDetectionStrategy, Component } from '@angular/core';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { aciesDateFormat, downloadFile } from '@shared/helpers';
import { GenericTableColumn, ISelectableData } from '@shared/models/generictablecolumn';
import { ITableMatrix, MatricesService } from '@shared/services/matrices.service';
import { NotificationService } from '@shared/services/notification.service';
import { finalize, map, Observable, Subject, take, tap } from 'rxjs';

@Component({
	selector: 'app-matrices',
	templateUrl: './matrices.component.html',
	styleUrl: './matrices.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class MatricesComponent {
	public isLoading: boolean = false;
	public loadingSubject: Subject<boolean> = new Subject<boolean>();
	public showSearch: boolean = false;
	public matricesFilter: string = '';
	public totalCount: number;
	public pageSizes = [10, 20, 50, 100];
	public initialPageSize = 20;
	public countSubject$: Subject<number> = new Subject<number>();
	public count$ = this.countSubject$.asObservable();
	public selected: object[] = [];
	public matricesData: Observable<ISelectableData<ITableMatrix>[]>;
	public userNames: Map<string, string> = new Map<string, string>();

	private unsubscribe: Subject<void> = new Subject<void>();

	public registerTableColumns: GenericTableColumn[] = [
		{ name: 'calculations.matrices.table.tags', dataKey: 'tags' },
		{
			name: 'calculations.matrices.table.totalCalculations',
			dataKey: 'totalCalculations',
		},
		{
			name: 'calculations.matrices.table.completedCalculations',
			dataKey: 'completedCalculations',
		},
		{
			name: 'calculations.matrices.table.status',
			dataKey: 'status',
			isStatusIcon: true,
		},
		{ name: 'calculations.matrices.table.created', dataKey: 'createdDate', sortKey: 'createdDateEpoch' },
	];

	public regRowActions: object[] = [
		{
			title: 'calculations.matrices.table.actions.downloadRaw',
			handler: 'downloadRawAssets',
		},
		{
			title: 'calculations.matrices.table.actions.downloadJson',
			handler: 'downloadJsonAssets',
		},
	];

	favoriteItems: SubMenuItem[] = [
		{
			icon: () => 'acies_renew',
			text: () => 'calculations.matrices.create.button',
			click: () => this.refreshData(),
		},
	];

	constructor(
		private matricesService: MatricesService,
		private notificationService: NotificationService,
	) {}

	ngOnInit(): void {
		this.loadData();
	}

	public ngOnDestroy() {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	loadData(): void {
		this.isLoading = true;
		this.matricesData = this.matricesService.getMatricesList().pipe(
			tap((data) => {
				this.isLoading = true;
				this.countSubject$.next(data.items.length);
			}),
			map((data) => {
				const items: ISelectableData<ITableMatrix>[] = data.items.map((p) => ({
					id: p.self, // Ensure the 'id' property is included
					self: p.self,
					totalCalculations: p.totalCalculations,
					completedCalculations: p.completedCalculations,
					status: p.status,
					tags: this.formatTagData(JSON.stringify(p.tags)),
					createdDateEpoch: p.meta.createdAt,
					createdDate: aciesDateFormat(new Date(p.meta.createdAt), true, false),
					outputType: '',
				}));
				items.sort((a, b) => b.createdDateEpoch - a.createdDateEpoch);
				return items;
			}),
			finalize(() => {
				this.isLoading = false;
			}),
		);
	}

	refreshData(): void {
		this.loadData();
	}

	handleReloadData(_: object): void {
		// Empty function to enable client side only pagination
		// this.loadData(data['pageSize'] * data['pageNumber']);
	}

	handleSelected(_: object[]): void {
		// this.selected = listOfSelected;
		// console.log('Selected:', this.selected);
	}

	formatTagData(tags: string): string {
		const data: any[] = JSON.parse(tags);
		let result = '';

		data.forEach((tag) => {
			result += `${tag.name}=${tag.value}, `;
		});

		if (result.length > 0) {
			result = result.substring(0, result.length - 2);
		}
		return result;
	}

	handleRowMenuAction(data: object): void {
		switch (data['action'].handler) {
			case 'downloadRawAssets':
				{
					console.log(data);
					const self = data['data']['self'];
					this.downloadRaw(self);
				}
				break;
			case 'downloadJsonAssets':
				{
					console.log(data);
					const self = data['data']['self'];
					this.downloadJson(self);
				}
				break;
			default:
				break;
		}
	}

	downloadRaw(self: string): void {
		this.matricesService
			.getDownloadUrl(self, 'raw')
			.pipe(take(1))
			.subscribe({
				next: (result) => {
					const downloadUrl = result.url;
					downloadFile(downloadUrl);
				},
				error: (error) => {
					if (error.message === 'Resource not found') {
						this.notificationService.notify(['calculations.matrices.notifications.noResults.header'], ['calculations.matrices.notifications.noResults.content'], 'error');
					}
				},
			});
	}

	downloadJson(self: string): void {
		this.matricesService
			.getDownloadUrl(self, 'json')
			.pipe(take(1))
			.subscribe({
				next: (result) => {
					const downloadUrl = result.url;
					downloadFile(downloadUrl);
				},
				error: (error) => {
					if (error.message === 'Resource not found') {
						this.notificationService.notify(['calculations.matrices.notifications.noResults.header'], ['calculations.matrices.notifications.noResults.content'], 'error');
					}
				},
			});
	}
}
